// Expense Report Hook
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { DateRange, ExpenseReport, ExpenseMetrics, ExpensesByCategory, ExpensesBySupplier } from '@/types/report';
import { calculateGrowthRate, calculateAverage, calculatePercentage } from '../utils/calculations';
import { getPreviousPeriod } from '../utils/periods';

export function useExpenseReport(period: DateRange) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['expense-report', company?.id, period.start.toISOString(), period.end.toISOString()],
    queryFn: async (): Promise<ExpenseReport> => {
      if (!company?.id) throw new Error('No company selected');

      // Fetch expense transactions for the period
      const { data: transactions, error } = await supabase
        .from('treasury_transactions')
        .select(`
          id,
          date,
          amount,
          type,
          category,
          client_id,
          notes,
          clients (
            id,
            name,
            type
          )
        `)
        .eq('company_id', company.id)
        .eq('type', 'expense')
        .gte('date', period.start.toISOString().split('T')[0])
        .lte('date', period.end.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) throw error;

      // Fetch previous period data
      const previousPeriod = getPreviousPeriod(period);
      const { data: previousTransactions } = await supabase
        .from('treasury_transactions')
        .select('amount')
        .eq('company_id', company.id)
        .eq('type', 'expense')
        .gte('date', previousPeriod.start.toISOString().split('T')[0])
        .lte('date', previousPeriod.end.toISOString().split('T')[0]);

      // Calculate metrics
      const totalExpenses = transactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;
      // Assume 19% TVA on expenses (simplified - in reality varies)
      const totalExpensesHT = Math.round((totalExpenses / 1.19) * 100) / 100;
      const totalTVA = totalExpenses - totalExpensesHT;
      const transactionCount = transactions?.length || 0;
      const averageExpense = calculateAverage(totalExpenses, transactionCount);

      const previousExpenses = previousTransactions?.reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;
      const growthRate = calculateGrowthRate(totalExpenses, previousExpenses);

      const metrics: ExpenseMetrics = {
        totalExpenses,
        totalExpensesHT,
        totalTVA,
        transactionCount,
        averageExpense,
        growthRate,
      };

      // Group by category
      const categoryMap = new Map<string, { amount: number; count: number }>();
      
      transactions?.forEach(tx => {
        const category = tx.category || 'Non catégorisé';
        const existing = categoryMap.get(category);
        
        if (existing) {
          existing.amount += tx.amount || 0;
          existing.count += 1;
        } else {
          categoryMap.set(category, {
            amount: tx.amount || 0,
            count: 1,
          });
        }
      });

      const byCategory: ExpensesByCategory[] = Array.from(categoryMap.entries())
        .map(([category, data]) => ({
          category,
          amount: Math.round(data.amount * 100) / 100,
          count: data.count,
          percentage: calculatePercentage(data.amount, totalExpenses),
        }))
        .sort((a, b) => b.amount - a.amount);

      // Group by supplier
      const supplierMap = new Map<string, { amount: number; count: number; name: string }>();
      
      transactions?.forEach(tx => {
        const client = tx.clients as any;
        if (!client || (client.type !== 'supplier' && client.type !== 'both')) return;

        const existing = supplierMap.get(client.id);
        if (existing) {
          existing.amount += tx.amount || 0;
          existing.count += 1;
        } else {
          supplierMap.set(client.id, {
            name: client.name,
            amount: tx.amount || 0,
            count: 1,
          });
        }
      });

      const bySupplier: ExpensesBySupplier[] = Array.from(supplierMap.entries())
        .map(([supplierId, data]) => ({
          supplierId,
          supplierName: data.name,
          amount: Math.round(data.amount * 100) / 100,
          count: data.count,
        }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 20); // Top 20 suppliers

      // Create trend data (daily aggregation)
      const trendMap = new Map<string, { amount: number; count: number }>();
      
      transactions?.forEach(tx => {
        const dateKey = tx.date;
        const existing = trendMap.get(dateKey);
        
        if (existing) {
          existing.amount += tx.amount || 0;
          existing.count += 1;
        } else {
          trendMap.set(dateKey, {
            amount: tx.amount || 0,
            count: 1,
          });
        }
      });

      const trend = Array.from(trendMap.entries())
        .map(([date, data]) => ({
          date,
          amount: Math.round(data.amount * 100) / 100,
          count: data.count,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      return {
        period,
        metrics,
        byCategory,
        bySupplier,
        trend,
      };
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
